export const DEFAULT_PLAYER_LIST_OPTIONS = {
  page: 1,
  limit: 15,
}

export const PLAYER_LIST_SEARCH_MIN_LENGTH = 2

const sortEntries = value =>
  Object.entries(value || {}).sort(([keyA], [keyB]) => keyA.localeCompare(keyB))

const levelFilterKeyPattern = /^gameLevel\[(singles|doubles)\]$/

const buildSortedObject = entries =>
  entries.reduce((acc, [key, value]) => {
    acc[key] = value
    return acc
  }, {})

export const normalizePlayerListOptions = options => {
  const page = Number(options?.page)
  const limit = Number(options?.limit)
  const sortBy = options?.sortBy

  return {
    page: page > 0 ? page : DEFAULT_PLAYER_LIST_OPTIONS.page,
    limit: limit > 0 ? limit : DEFAULT_PLAYER_LIST_OPTIONS.limit,
    ...(sortBy ? { sortBy } : {}),
  }
}

export const resetPlayerListPage = options => ({
  ...normalizePlayerListOptions(options),
  page: 1,
})

const normalizePlayerListStateFilters = filters =>
  sortEntries(filters).reduce((acc, [key, value]) => {
    if (value === undefined || value === null || value === "") return acc

    if (key === "search") {
      const search = String(value).trim()
      if (search) acc.search = search
      return acc
    }

    acc[key] = value
    return acc
  }, {})

export const normalizePlayerListFilters = filters => {
  const gameLevel = {}
  const queryFilters = sortEntries(
    normalizePlayerListStateFilters(filters)
  ).reduce((acc, [key, value]) => {
    const levelKey = key.match(levelFilterKeyPattern)?.[1]

    if (levelKey) {
      gameLevel[levelKey] = String(value)
      return acc
    }

    acc[key] = value
    return acc
  }, {})

  const normalizedGameLevel = {}
  if (gameLevel.singles) normalizedGameLevel.singles = gameLevel.singles
  if (gameLevel.doubles) normalizedGameLevel.doubles = gameLevel.doubles

  if (Object.keys(normalizedGameLevel).length) {
    queryFilters.gameLevel = JSON.stringify(normalizedGameLevel)
  }

  return buildSortedObject(sortEntries(queryFilters))
}

export const removePlayersSearchFilter = filters => {
  const { search, ...rest } = normalizePlayerListFilters(filters)
  return rest
}

const getSearchText = filters => String(filters?.search || "").trim()

export const shouldFetchPlayersList = filters => {
  const search = getSearchText(filters)
  return !search || search.length >= PLAYER_LIST_SEARCH_MIN_LENGTH
}

export const getPlayersLocationFilters = filters =>
  removePlayersSearchFilter(filters)

export const applyPlayersFilterChange = ({ nextFilters, options }) => ({
  filters: normalizePlayerListStateFilters(nextFilters),
  options: resetPlayerListPage(options),
})

export const applyPlayersSortChange = ({ options, sortBy }) => {
  const normalizedOptions = normalizePlayerListOptions(options)

  if (!sortBy || sortBy === normalizedOptions.sortBy) {
    return { shouldUpdate: false }
  }

  return {
    options: {
      ...resetPlayerListPage(normalizedOptions),
      sortBy,
    },
    shouldUpdate: true,
  }
}

export const applyPlayersSearchChange = ({ filters, options, searchText }) => {
  const inputText = String(searchText || "")
  const search = inputText.trim()

  if (search && search.length < PLAYER_LIST_SEARCH_MIN_LENGTH) {
    return { inputText, shouldUpdate: false }
  }

  if (!search) {
    return {
      filters: removePlayersSearchFilter(filters),
      inputText,
      options: resetPlayerListPage(options),
      searchText: "",
      shouldUpdate: true,
    }
  }

  return {
    filters: {
      ...removePlayersSearchFilter(filters),
      search,
    },
    inputText,
    options: resetPlayerListPage(options),
    searchText: search,
    shouldUpdate: true,
  }
}
